import { useEffect, useCallback, useMemo } from 'react';

import { useDeviceType } from '@picsart/design-system/hooks/device';
import { useAppStore } from '@/store';
import { getDeeplinkSnapshot, DeeplinkKind } from '@/features/deeplinks';
import useLibrarySectionsQuery from './queries/useLibrarySectionsQuery';
import useLibraryItemInfoMutation from './queries/useLibraryItemInfoMutation';

import type { IAppStore } from '@/store';

const selector = (store: IAppStore) => ({
  activeTabSlug: store.activeTabSlug,
  setActiveTabSlug: store.setActiveTabSlug,
  selectedPreset: store.selectedPreset,
  hasAutoSelectedPreset: store.hasAutoSelectedPreset,
  setHasAutoSelectedPreset: store.setHasAutoSelectedPreset,
  isPresetLoading: store.isPresetLoading,
  setSelectedPreset: store.setSelectedPreset,
  setInputs: store.setInputs,
});

const useLibrary = () => {
  const {
    activeTabSlug,
    setActiveTabSlug,
    selectedPreset,
    hasAutoSelectedPreset,
    setHasAutoSelectedPreset,
  } = useAppStore(selector);

  const { data: sections, isLoading } = useLibrarySectionsQuery();
  const presetMutation = useLibraryItemInfoMutation();

  const { isMobile } = useDeviceType();

  const tabs = useMemo(
    () => (sections ?? []).map(({ title, slug }) => ({ title, slug })),
    [sections]
  );
  const items = useMemo(
    () =>
      sections?.find((section) => section.slug === activeTabSlug)?.items ?? [],
    [sections, activeTabSlug]
  );

  // Set first tab as active when tabs are loaded
  useEffect(() => {
    if (tabs.length > 0 && !activeTabSlug) {
      setActiveTabSlug(tabs[0].slug);
    }
  }, [tabs, activeTabSlug, setActiveTabSlug]);

  // `projectJsonHandler` can only claim `hasAutoSelectedPreset` after fetching
  // its JSON, so the store flag lands too late to block this auto-select —
  // hence the snapshot check. `presetHandler` claims synchronously (it runs
  // before this hook's effect and long before `items` arrives), so a preset
  // deeplink needs no permanent guard: its failure branch releases the flag and
  // the library falls back to the first preset.
  const deeplinkSelectsPreset =
    getDeeplinkSnapshot().kind === DeeplinkKind.ProjectJson;

  // Auto-select first preset on desktop only (mobile shows gallery first)
  // Uses store flag instead of ref so state persists across component remounts
  // Note: the deeplink router's presetHandler sets hasAutoSelectedPreset
  // early to prevent racing this auto-select.
  useEffect(() => {
    // Auto-select on tablet+ (≥768px); mobile sees the gallery first.
    if (
      !isMobile &&
      !deeplinkSelectsPreset &&
      !hasAutoSelectedPreset &&
      items.length > 0 &&
      !selectedPreset &&
      !isLoading
    ) {
      setHasAutoSelectedPreset(true);
      const firstItem = items[0];
      if (firstItem?.slug) {
        presetMutation.mutate(firstItem.slug);
      }
    }
  }, [
    isMobile,
    deeplinkSelectsPreset,
    items,
    selectedPreset,
    isLoading,
    presetMutation,
    hasAutoSelectedPreset,
    setHasAutoSelectedPreset,
  ]);

  return {
    tabs,
    activeTabSlug,
    items,
    isLoading,
  };
};

const usePreset = () => {
  const { selectedPreset, isPresetLoading, setSelectedPreset, setInputs } =
    useAppStore(selector);

  const presetMutation = useLibraryItemInfoMutation();
  const presetMutate = presetMutation.mutate;

  const loadPreset = useCallback(
    (slug: string) => presetMutate(slug),
    [presetMutate]
  );

  const clearPreset = useCallback(() => {
    setSelectedPreset(null);
    setInputs([]);
  }, [setSelectedPreset, setInputs]);

  return {
    preset: selectedPreset,
    isLoading: isPresetLoading || presetMutation.isPending,
    loadPreset,
    clearPreset,
  };
};

export { useLibrary, usePreset };
